/**
 * toque — os três sons das ligações do chat (Task 10, 24/10/2026; o bipe de espera, 25/10/2026).
 *
 * - `tocarCampainha`: para quem RECEBE. Campainha de telefone ("ring-ring… pausa… ring-ring…"), em
 *   laço até o `stop`, ALTA de propósito: o pedido do dono do produto foi "toque alto para as
 *   pessoas escutarem bem". É o oposto do "plim" do aviso (`somDeAviso`), que é curto e baixo.
 * - `tocarChamando`: para quem LIGA. O "tuuu" baixo do telefone chamando do outro lado: 425 Hz,
 *   1 s ligado e 4 s desligado (o tom de controle de chamada do Brasil).
 * - `tocarBipeDeEspera`: para quem RECEBE uma ligação estando JÁ em outra (decisão do dono do produto, 25/10).
 *   O "bi-bip" da chamada em espera no lugar da campainha alta: duas notas de 0,1 s (1046,5 Hz) a cada 5 s,
 *   baixo como o "tuuu". Segue a campainha em tudo o mais: mesma trava, mesmo teto, mesma espera pelo áudio.
 *
 * Os três sintetizam no Web Audio (sem arquivo) e reaproveitam o contexto da aba
 * (`contextoDaAba`), o mesmo que o `somDeAviso` desbloqueia no 1º gesto.
 *
 * 🔊 Volume sem distorção. A campainha soma dois parciais (um triangular de ~1,2–1,5 kHz e um
 * brilho senoidal uma oitava acima, onde o ouvido é mais sensível) cujo pico chega a ~1,2: de
 * propósito acima de 1, para soar forte. O `DynamicsCompressorNode` no caminho para o destino
 * segura esse pico (com o ganho de compensação automático da API) em vez de deixar a placa
 * cortar a onda. O "chamando" e o bipe de espera NÃO passam por ele: são baixos por desenho, e a
 * compensação os subiria.
 *
 * 🔔 Uma campainha por aparelho. Com várias abas abertas o evento chega a todas; só toca a aba que
 * pega a trava `navigator.locks` "seweb:campainha" (`ifAvailable`: quem não pega NÃO entra na
 * fila e mostra o aviso mudo). A trava fica com ela até o `stop`; fechar a aba também a solta.
 * O bipe de espera disputa a MESMA trava. Soltá-la é assíncrono, e o React troca o efeito parando
 * a campainha e já tocando o bipe: o som novo espera a soltura do anterior (só quando há uma
 * pendente, com teto de ~1 s) antes de pedir a trava, ou receberia `null` e ficaria mudo.
 * Sem `navigator.locks` (navegador antigo, http fora de localhost) toca, e o risco é tocar em
 * todas as abas.
 *
 * 📞 Quem já está em ligação ouve o bipe, em QUALQUER aba do aparelho (decisão do dono do produto, 25/10). A escolha
 * do som é da aba do provider (`isInCall` dela), mas quem toca é a aba que ganha a trava, e ela pode ser outra: com a
 * ligação na aba A e a B em outro módulo, a campainha ALTA tocaria no meio da conversa. Por isso a aba em ligação
 * segura a trava compartilhada "seweb:em-ligacao" (`marcarEmLigacao`), e a campainha, ao ganhar a trava, consulta
 * `navigator.locks.query()`: com "seweb:em-ligacao" em `held` ela toca o bipe. A consulta se repete a cada passo do
 * laço, então a campainha vira bipe se OUTRA aba entra em ligação no meio do toque (e volta se a ligação acaba).
 * Sem `locks`, ou sem `query`, fica como antes: a escolha é só da aba.
 *
 * 🔒 Só concorre à trava a aba que PODE soar: com o áudio `running`. Uma aba restaurada e nunca
 * clicada tem o contexto suspenso; se ela pegasse a trava (a ordem de chegada é arbitrária),
 * seguraria a vez em silêncio e nenhuma aba soaria — o mesmo cuidado da `travaDoSom` do D1 ("a aba
 * só concorre depois do SEU 1º gesto"). Aba com o áudio suspenso não pede a trava e não soa; ela
 * tenta `resume()` a cada passo e, se a pessoa clicar enquanto toca, passa a concorrer ali.
 *
 * ⏱️ A campainha também para sozinha depois de `TETO_DA_CAMPAINHA_MS` e solta a trava. O servidor
 * encerra o toque aos 30 s; o teto é a rede de segurança para o `chamada:parou` que se perde: sem
 * ele a campainha tocaria sem parar e, segurando a trava, silenciaria as ligações seguintes.
 *
 * ⚠️ Autoplay: contexto que não está `running` não soa. Em vez de agendar num contexto suspenso
 * (o som sairia atrasado, no próximo clique), o laço tenta `resume()` a cada passo e só agenda
 * quando o áudio estiver liberado — se a pessoa clicar enquanto toca, a campainha começa ali.
 *
 * Nunca lançam: o som é o enfeite da chamada; o aviso na tela chega de qualquer jeito. O `stop`
 * devolvido é idempotente.
 */
import { contextoDaAba } from "@/lib/somDeAviso";

const NOME_DA_TRAVA_DA_CAMPAINHA = "seweb:campainha";
/** Segurada (em modo compartilhado) por toda aba que está numa ligação: é o sinal "este aparelho está em ligação". */
const NOME_DA_TRAVA_EM_LIGACAO = "seweb:em-ligacao";
/**
 * Quanto tempo a campainha toca no máximo, mesmo sem `stop`. O servidor encerra o toque aos 30 s
 * (`TEMPO_DE_TOQUE`); 45 s deixa folga para o `stop` normal chegar primeiro e só pega o evento
 * `chamada:parou` perdido.
 */
const TETO_DA_CAMPAINHA_MS = 45_000;
/**
 * Quanto um som novo espera, no máximo, a trava do som anterior desta aba ser solta antes de pedi-la mesmo
 * assim: a soltura que o navegador nunca conclui não pode deixar o som mudo para sempre.
 */
const TETO_DA_ESPERA_PELA_SOLTURA_MS = 1_000;

/** De quanto em quanto tempo o laço confere se há ciclo a agendar. */
const PASSO_DO_LACO_MS = 500;
/** Quanto do futuro (s, no relógio do áudio) fica agendado: sobrevive a um temporizador atrasado. */
const ANTECEDENCIA_S = 2;
/** Fim do envelope: o `exponentialRamp` não chega a 0. */
const SILENCIO = 0.0001;
const ATAQUE_S = 0.01;
const SOLTA_S = 0.03;
/** Folga entre "agora" e o 1º ciclo agendado, para o agendamento não chegar atrasado ao áudio. */
const ATRASO_DO_PRIMEIRO_CICLO_S = 0.02;
/** Margem depois do fim do envelope para o oscilador parar sem estalo. */
const FOLGA_DO_STOP_S = 0.02;
/** Descida do volume geral ao parar, e quando o grafo é desligado (depois da descida). */
const DESCIDA_AO_PARAR_S = 0.03;
const DESLIGAR_DEPOIS_MS = 150;

interface Parcial {
  tipo: OscillatorType;
  /** Uma só = tom fixo; mais de uma = a nota alterna entre elas a cada `passoDaOscilacao`. */
  frequencias: readonly number[];
  pico: number;
}

interface Som {
  /** Do começo de um ciclo ao começo do seguinte (s). */
  periodo: number;
  /** Os toques de um ciclo, em segundos desde o começo dele. */
  toques: readonly { inicio: number; duracao: number }[];
  parciais: readonly Parcial[];
  passoDaOscilacao: number;
  comCompressor: boolean;
}

/**
 * "Ring-ring": dois toques de 0,4 s com 0,2 s de pausa, e silêncio até fechar 3 s. Cada toque
 * oscila entre duas notas (trilo de telefone), que corta o ruído do escritório melhor que uma
 * nota parada.
 */
const CAMPAINHA: Som = {
  periodo: 3,
  toques: [
    { inicio: 0, duracao: 0.4 },
    { inicio: 0.6, duracao: 0.4 },
  ],
  parciais: [
    { tipo: "triangle", frequencias: [1200, 1500], pico: 0.8 },
    { tipo: "sine", frequencias: [2400, 3000], pico: 0.4 },
  ],
  passoDaOscilacao: 0.04,
  comCompressor: true,
};

/** O "tuuu" do telefone chamando: 425 Hz, 1 s ligado, 4 s desligado, baixo. */
const CHAMANDO: Som = {
  periodo: 5,
  toques: [{ inicio: 0, duracao: 1 }],
  parciais: [{ tipo: "sine", frequencias: [425], pico: 0.1 }],
  passoDaOscilacao: 1,
  comCompressor: false,
};

/**
 * O bipe de "chamada em espera": duas notas curtas e baixas a cada 5 s ("bi-bip"), para quem já está numa
 * ligação quando outra chega. 1046,5 Hz (C6, a nota do "plim" do aviso) passa por cima da fala sem precisar de
 * volume; seno puro não tem harmônico áspero para o microfone pegar; 425 Hz está descartado porque é o "tuuu" de
 * quem liga (a pessoa pode estar discando quando a segunda chega). Pico 0,1 e sem compressor, como o "tuuu": o
 * compressor subiria o ganho de compensação e o bipe deixaria de ser baixo.
 */
const ESPERA: Som = {
  periodo: 5,
  toques: [
    { inicio: 0, duracao: 0.1 },
    { inicio: 0.2, duracao: 0.1 },
  ],
  parciais: [{ tipo: "sine", frequencias: [1046.5], pico: 0.1 }],
  passoDaOscilacao: 1,
  comCompressor: false,
};

function agendarToque(
  ctx: AudioContext,
  destino: AudioNode,
  som: Som,
  parcial: Parcial,
  inicio: number,
  duracao: number
): void {
  const osc = ctx.createOscillator();
  const ganho = ctx.createGain();
  osc.type = parcial.tipo;
  const { frequencias } = parcial;
  if (frequencias.length === 1) {
    osc.frequency.setValueAtTime(frequencias[0], inicio);
  } else {
    for (let i = 0; i * som.passoDaOscilacao < duracao; i += 1) {
      osc.frequency.setValueAtTime(
        frequencias[i % frequencias.length],
        inicio + i * som.passoDaOscilacao
      );
    }
  }
  // ⚠️ Sobe de 0,0001 em vez de começar no pico, e desce antes de parar: o degrau seco estala.
  ganho.gain.setValueAtTime(SILENCIO, inicio);
  ganho.gain.exponentialRampToValueAtTime(parcial.pico, inicio + ATAQUE_S);
  ganho.gain.setValueAtTime(parcial.pico, inicio + duracao - SOLTA_S);
  ganho.gain.exponentialRampToValueAtTime(SILENCIO, inicio + duracao);
  osc.connect(ganho).connect(destino);
  osc.start(inicio);
  osc.stop(inicio + duracao + FOLGA_DO_STOP_S);
}

function agendarCiclo(ctx: AudioContext, destino: AudioNode, som: Som, inicio: number): void {
  for (const toque of som.toques) {
    for (const parcial of som.parciais) {
      agendarToque(ctx, destino, som, parcial, inicio + toque.inicio, toque.duracao);
    }
  }
}

function silenciosa(): void {
  // Sem Web Audio, ou com ele quebrado: não há o que parar.
}

interface Grafo {
  audio: AudioContext;
  /** Volume geral do som: é o que desce ao parar. */
  mestre: GainNode;
  compressor: DynamicsCompressorNode | null;
}

/** `mestre` → (compressor) → destino, no contexto da aba. `null` sem Web Audio ou com ele quebrado. */
function montarGrafo(comCompressor: boolean): Grafo | null {
  try {
    const audio = contextoDaAba();
    if (!audio) return null;
    const mestre = audio.createGain();
    mestre.gain.value = 1;
    let compressor: DynamicsCompressorNode | null = null;
    if (comCompressor) {
      compressor = audio.createDynamicsCompressor();
      compressor.threshold.value = -12;
      compressor.knee.value = 10;
      compressor.ratio.value = 6;
      compressor.attack.value = 0.003;
      compressor.release.value = 0.1;
      mestre.connect(compressor);
      compressor.connect(audio.destination);
    } else {
      mestre.connect(audio.destination);
    }
    return { audio, mestre, compressor };
  } catch {
    return null;
  }
}

/**
 * Começa o laço do som e devolve o `stop`. Os ciclos são agendados no relógio do áudio
 * (preciso), com pouca antecedência, por um temporizador que só confere de tempos em tempos.
 */
function iniciarLaco(som: Som): () => void {
  const grafo = montarGrafo(som.comCompressor);
  if (!grafo) return silenciosa;
  const { audio, mestre, compressor } = grafo;
  let parado = false;
  /** Quando começa o próximo ciclo ainda não agendado (relógio do áudio). */
  let proximoCiclo = 0;

  const passo = () => {
    if (parado) return;
    try {
      if (audio.state !== "running") {
        // Fora de um gesto o navegador recusa; o 1º clique da pessoa (`manterAudioRetomado`) libera.
        void audio.resume().catch(() => undefined);
        return;
      }
      const agora = audio.currentTime;
      // Primeira vez, ou o temporizador atrasou e o ciclo ficou no passado: recomeça daqui.
      if (proximoCiclo <= agora) proximoCiclo = agora + ATRASO_DO_PRIMEIRO_CICLO_S;
      while (proximoCiclo < agora + ANTECEDENCIA_S) {
        agendarCiclo(audio, mestre, som, proximoCiclo);
        proximoCiclo += som.periodo;
      }
    } catch {
      // Sem som neste passo; o próximo tenta de novo.
    }
  };

  passo();
  const temporizador = setInterval(passo, PASSO_DO_LACO_MS);

  return () => {
    if (parado) return;
    parado = true;
    clearInterval(temporizador);
    try {
      // Corta o que já está agendado à frente: o volume geral desce em instantes e o grafo sai
      // do ar logo depois (os osciladores restantes acabam sozinhos, mudos).
      const agora = audio.currentTime;
      mestre.gain.cancelScheduledValues(agora);
      mestre.gain.setValueAtTime(mestre.gain.value, agora);
      mestre.gain.linearRampToValueAtTime(0, agora + DESCIDA_AO_PARAR_S);
      setTimeout(() => {
        try {
          mestre.disconnect();
          compressor?.disconnect();
        } catch {
          // Já desligado.
        }
      }, DESLIGAR_DEPOIS_MS);
    } catch {
      // Sem como cortar com suavidade: desliga na hora.
      try {
        mestre.disconnect();
      } catch {
        // Já desligado.
      }
    }
  };
}

interface Travas {
  request: (
    nome: string,
    opcoes: { ifAvailable?: boolean; mode?: "shared" | "exclusive" },
    aoObter: (trava: unknown) => Promise<void> | undefined
  ) => Promise<unknown>;
  /** `LockManager.query`: as travas seguradas no aparelho. Ausente em navegador antigo (e em dublê). */
  query?: () => Promise<{ held?: ReadonlyArray<{ name?: string }> } | undefined>;
}

function travasDoNavegador(): Travas | null {
  try {
    const travas = (navigator as unknown as { locks?: Travas | null }).locks;
    // happy-dom e navegador antigo: `locks` existe e vale null.
    return travas && typeof travas.request === "function" ? travas : null;
  } catch {
    return null;
  }
}

/**
 * Alguma aba do aparelho está em ligação? Lê as travas seguradas (`navigator.locks.query()`). `null` quando não dá
 * para saber (sem `query`, ou ela falhou): quem chama mantém o que já toca.
 */
async function aparelhoEmLigacao(travas: Travas): Promise<boolean | null> {
  if (typeof travas.query !== "function") return null;
  try {
    // Chamada como método: `query` precisa do `this` do `navigator.locks`.
    const estado = await travas.query();
    return (estado?.held ?? []).some((trava) => trava?.name === NOME_DA_TRAVA_EM_LIGACAO);
  } catch {
    return null;
  }
}

/**
 * Avisa ao aparelho que esta aba está numa ligação (ver o cabeçalho): segura "seweb:em-ligacao" em modo compartilhado
 * (várias abas podem estar em ligação ao mesmo tempo) até o `soltar` devolvido, que é idempotente. Fechar a aba também
 * solta a trava. Nunca lança; sem `navigator.locks` não faz nada.
 */
export function marcarEmLigacao(): () => void {
  const travas = travasDoNavegador();
  if (!travas) return silenciosa;
  let soltou = false;
  let soltarTrava: (() => void) | null = null;
  try {
    void Promise.resolve(
      travas.request(NOME_DA_TRAVA_EM_LIGACAO, { mode: "shared" }, () => {
        // Soltou antes de a trava chegar: a devolve na hora.
        if (soltou) return undefined;
        return new Promise<void>((resolve) => {
          soltarTrava = resolve;
        });
      })
    ).catch(() => undefined);
  } catch {
    // Navegador com `locks` quebrado: sem o sinal, vale só a escolha da aba.
    return silenciosa;
  }
  return () => {
    if (soltou) return;
    soltou = true;
    soltarTrava?.();
    soltarTrava = null;
  };
}

/** O estado do áudio da aba; `null` sem Web Audio (nada pode soar). */
function estadoDoAudio(): string | null {
  try {
    return contextoDaAba()?.state ?? null;
  } catch {
    return null;
  }
}

/**
 * A soltura da trava pelo ÚLTIMO som parado desta aba, enquanto o navegador ainda não a concluiu; `null` sem
 * pendência. Soltar a trava (resolver a promessa do `request`) é assíncrono: quem para a campainha e logo toca o
 * bipe (o React troca o efeito assim) pediria a trava ainda ocupada pela própria aba, receberia `null` com
 * `ifAvailable` e ficaria mudo. Por isso o pedido seguinte espera esta promessa. Nunca rejeita.
 */
let solturaPendente: Promise<void> | null = null;

function anotarSoltura(pedido: Promise<unknown>): void {
  const soltura = pedido.then(
    () => undefined,
    () => undefined
  );
  solturaPendente = soltura;
  void soltura.then(() => {
    if (solturaPendente === soltura) solturaPendente = null;
  });
}

/** Só para os testes: esquece a soltura pendente da trava (o estado do módulo não vaza entre testes). */
export function _reiniciarTravaParaTeste(): void {
  solturaPendente = null;
}

/**
 * Toca `som` em laço até o `stop` (ou o teto), com a trava de uma-por-aparelho (ver o cabeçalho): a aba que
 * não pega a trava não toca, e só pede a trava quem tem o áudio liberado. O `stop` solta a trava. É o corpo
 * da campainha e do bipe de espera: os dois disputam a MESMA trava.
 *
 * `quandoEmLigacao`: o som que toma o lugar de `som` enquanto OUTRA aba do aparelho está em ligação (a campainha
 * passa o bipe; o próprio bipe passa `null`, pois já é o som de quem está em ligação). Só vale depois de ganhar a
 * trava e só com `navigator.locks.query()`; a consulta se repete a cada passo do laço.
 */
function tocarComTrava(som: Som, quandoEmLigacao: Som | null): () => void {
  let parou = false;
  let pararSom: (() => void) | null = null;
  /** O som que o laço toca agora: `som`, ou `quandoEmLigacao` enquanto o aparelho está em ligação. */
  let somAtual = som;
  let conferenciaDaLigacao: ReturnType<typeof setInterval> | null = null;
  let soltarTrava: (() => void) | null = null;
  let esperaDoAudio: ReturnType<typeof setInterval> | null = null;
  /** A espera pela soltura da trava do som anterior (ver `solturaPendente`); sai junto com o `stop`. */
  let esperaDaSoltura: ReturnType<typeof setTimeout> | null = null;
  /** O `request` desta aba que ainda não concluiu: é a soltura que o próximo som vai esperar se este parar. */
  let pedidoEmVoo: Promise<unknown> | null = null;

  const tocar = (qual: Som) => {
    somAtual = qual;
    pararSom = iniciarLaco(qual);
  };

  const comecar = () => {
    if (parou || pararSom) return;
    tocar(som);
  };

  /**
   * Passo da conferência: o aparelho entrou (ou saiu) de uma ligação no meio do toque? Troca o laço pelo som certo; a
   * consulta que não responde mantém o que toca. A trava da campainha continua com esta aba: só o som muda.
   */
  const reconferirALigacao = (travas: Travas, alternativo: Som) => {
    void aparelhoEmLigacao(travas).then((emLigacao) => {
      if (parou || !pararSom || emLigacao === null) return;
      const certo = emLigacao ? alternativo : som;
      if (certo === somAtual) return;
      pararSom();
      tocar(certo);
    });
  };

  /**
   * Ganhou a trava da campainha: começa a tocar o som que o aparelho pede. Com `query` pergunta ANTES de tocar (um
   * ciclo da campainha por engano seria alto em quem está em ligação) e passa a reconferir a cada passo.
   */
  const comecarComATrava = (travas: Travas) => {
    if (parou || pararSom) return;
    const alternativo = quandoEmLigacao;
    if (!alternativo || typeof travas.query !== "function") {
      comecar();
      return;
    }
    void aparelhoEmLigacao(travas).then((emLigacao) => {
      if (parou || pararSom) return;
      tocar(emLigacao ? alternativo : som);
      conferenciaDaLigacao = setInterval(
        () => reconferirALigacao(travas, alternativo),
        PASSO_DO_LACO_MS
      );
    });
  };

  const enviarOPedido = (travas: Travas) => {
    try {
      const pedido = Promise.resolve(
        travas.request(NOME_DA_TRAVA_DA_CAMPAINHA, { ifAvailable: true }, (trava) => {
          // Outra aba já toca (`null`), ou esta já desistiu antes de a trava chegar: a devolve na hora.
          if (!trava || parou) return undefined;
          comecarComATrava(travas);
          return new Promise<void>((resolve) => {
            soltarTrava = resolve;
          });
        })
      ).catch(() => comecar());
      pedidoEmVoo = pedido;
      const concluir = () => {
        if (pedidoEmVoo === pedido) pedidoEmVoo = null;
      };
      void pedido.then(concluir, concluir);
    } catch {
      // Navegador com `locks` quebrado: toca como se não houvesse.
      comecar();
    }
  };

  const pedirATrava = (travas: Travas) => {
    const pendente = solturaPendente;
    // Sem soltura pendente o pedido sai já, na própria chamada (nada de microtarefa a mais).
    if (pendente === null) {
      enviarOPedido(travas);
      return;
    }
    let seguiu = false;
    const seguir = () => {
      if (seguiu) return;
      seguiu = true;
      if (esperaDaSoltura) clearTimeout(esperaDaSoltura);
      esperaDaSoltura = null;
      if (!parou) enviarOPedido(travas);
    };
    esperaDaSoltura = setTimeout(seguir, TETO_DA_ESPERA_PELA_SOLTURA_MS);
    void pendente.then(seguir);
  };

  /**
   * Um passo da espera pelo áudio: com ele `running`, pede a trava (e a espera acaba); suspenso,
   * tenta `resume()` (o navegador só aceita depois de um gesto). Devolve se a espera terminou.
   */
  const conferirOAudio = (travas: Travas): boolean => {
    if (parou) return true;
    const estado = estadoDoAudio();
    // Sem Web Audio nada soa, então não há o que disputar.
    if (estado === null) return true;
    if (estado === "running") {
      pedirATrava(travas);
      return true;
    }
    try {
      void contextoDaAba()
        ?.resume()
        .catch(() => undefined);
    } catch {
      // Sem como retomar agora; o próximo passo tenta de novo.
    }
    return false;
  };

  let teto: ReturnType<typeof setTimeout> | null = null;

  const parar = () => {
    if (parou) return;
    parou = true;
    if (teto) clearTimeout(teto);
    teto = null;
    if (esperaDoAudio) clearInterval(esperaDoAudio);
    esperaDoAudio = null;
    if (conferenciaDaLigacao) clearInterval(conferenciaDaLigacao);
    conferenciaDaLigacao = null;
    if (esperaDaSoltura) clearTimeout(esperaDaSoltura);
    esperaDaSoltura = null;
    pararSom?.();
    pararSom = null;
    soltarTrava?.();
    soltarTrava = null;
    // A trava só volta ao navegador depois deste `stop`: o próximo som desta aba espera por isso.
    if (pedidoEmVoo) anotarSoltura(pedidoEmVoo);
  };

  const travas = travasDoNavegador();
  if (!travas) {
    comecar();
  } else if (!conferirOAudio(travas)) {
    esperaDoAudio = setInterval(() => {
      if (conferirOAudio(travas) && esperaDoAudio) {
        clearInterval(esperaDoAudio);
        esperaDoAudio = null;
      }
    }, PASSO_DO_LACO_MS);
  }

  // Teto de segurança (ver o cabeçalho): para sozinha, como se fosse o `stop`.
  teto = setTimeout(parar, TETO_DA_CAMPAINHA_MS);

  return parar;
}

/**
 * A campainha de quem recebe a ligação: toca em laço, ALTO, até o `stop` (ou o teto). Uma por
 * aparelho (ver o cabeçalho). O `stop` solta a trava. Se outra aba do aparelho está em ligação, toca o bipe de espera
 * no lugar (e volta à campainha quando a ligação dela acaba).
 */
export function tocarCampainha(): () => void {
  return tocarComTrava(CAMPAINHA, ESPERA);
}

/**
 * O bipe de quem JÁ está numa ligação quando outra chega: curto e baixo, no lugar da campainha alta. Mesma
 * trava, mesma espera pelo áudio e mesmo teto de 45 s da campainha: uma aba só por aparelho vale para os dois.
 */
export function tocarBipeDeEspera(): () => void {
  return tocarComTrava(ESPERA, null);
}

/** O "tuuu" de quem liga, enquanto o outro lado toca: 1 s ligado e 4 s desligado, baixo. */
export function tocarChamando(): () => void {
  return iniciarLaco(CHAMANDO);
}
